// Platform / WebView detection.
// ----------------------------------------------------------------------------
// We need a reliable signal for "is this page being rendered inside a native
// Android/iOS wrapper (Capacitor, Cordova, Expo WebView, generic WebView)?"
// so we can hide Stripe purchase buttons on those platforms and stay
// compliant with Google Play Billing / Apple IAP rules.
//
// We err on the side of detecting "in-app" → if any signal trips, we treat
// the user as in-app. Webview-only TWAs (Trusted Web Activities) installed
// from Play also count, since reviewers may install them that way during
// review.
//
// Public API:
//   isAndroidWebView() → true when running inside the Android native wrapper
//   isIOSWebView()     → true when running inside the iOS native wrapper
//   isInNativeApp()    → true if either of the above
//   shouldHidePaywall()→ true when we MUST hide the Stripe purchase CTAs
//                        because the host platform requires native billing
//
// All checks are no-op on SSR (return false).
const ua = () =>
  (typeof navigator !== "undefined" && (navigator.userAgent || "")).toLowerCase();

export function isCapacitor() {
  return typeof window !== "undefined" && !!window.Capacitor;
}

export function isCordova() {
  return typeof window !== "undefined" && !!window.cordova;
}

export function isAndroidWebView() {
  if (typeof window === "undefined") return false;
  const u = ua();
  // 1. Explicit native bridges installed by Capacitor / Cordova / TWAs
  if (isCapacitor() || isCordova()) {
    return /android/.test(u);
  }
  // 2. Generic WebView UA — Android sets ";wv)" inside the UA when the
  //    page is rendered inside an embedded WebView. Chrome/Firefox tabs
  //    do NOT set this flag.
  if (/android/.test(u) && /;\s*wv\)/.test(u)) return true;
  // 3. Trusted Web Activity — Chrome sets a `chrome-search:` referrer when
  //    launched from a TWA, and the standalone display mode is on.
  if (typeof window.matchMedia === "function") {
    const standalone = window.matchMedia("(display-mode: standalone)").matches;
    if (standalone && /android/.test(u)) return true;
  }
  return false;
}

export function isIOSWebView() {
  if (typeof window === "undefined") return false;
  const u = ua();
  const isIOS = /iphone|ipad|ipod/.test(u);
  if (!isIOS) return false;
  if (isCapacitor() || isCordova()) return true;
  // iOS WebViews don't set Safari in the UA string. Safari tabs DO.
  return !/safari/.test(u);
}

export function isInNativeApp() {
  return isAndroidWebView() || isIOSWebView();
}

// Google Play and Apple App Store both forbid third-party billing for
// digital goods inside a native app. When we detect we're in one of those
// shells we hide the Stripe purchase CTAs and tell the user to subscribe
// via the website instead. Users who reach holywords144.com in a regular
// browser tab still see the full pricing grid.
export function shouldHidePaywall() {
  return isInNativeApp();
}
